import { MedplumClient } from "@medplum/core";
import type { Coding, Parameters as FhirParameters } from "@medplum/fhirtypes";

import * as Sentry from "@sentry/react";

import { ProceduresValueSets } from "@/fhir/enums/procedures";
import { isHcpcsLevelIICode } from "@/shared/billable-procedure-codes";
import { getValueSetOptions } from "@/utils/medplum";

import { CPT_CODE_SYSTEM, HCPCS_CODE_SYSTEM } from "../constants/combined-procedure-codes";
import { isBillableHostedCoding } from "../utils/billable-hosted-coding";

/**
 * Medplum `fhirUrl` joins with `/`; unescaped `$` in a segment is interpreted by the URL
 * constructor, so the FHIR operation path must use an encoded dollar sign.
 */
const VALUE_SET_VALIDATE_CODE_PATH = "ValueSet/%24validate-code";

/**
 * Max rows we request when filtering the billable procedure codes ValueSet by the entered code,
 * used only when {@link VALUE_SET_VALIDATE_CODE_PATH} is unavailable. The filter is substring-based
 * on code/display; we then require an exact `code` match client-side. If the server returns this
 * many rows, we may not have seen every matching row — see
 * {@link CustomProcedureCptCodeCheckResult} `truncated`.
 *
 * Exported for tests and operators tuning filter volume against server limits.
 */
export const CPT_PROCEDURE_CODE_CONFLICT_FILTER_MAX_RESULTS = 5000;

export type CustomProcedureCptCodeCheckResult =
  | {
      status: "verified";
      /** True when the entered code is a CPT or HCPCS Level II code in the billable ValueSet. */
      conflicts: boolean;
      /** True only when the expand-based fallback ran and hit {@link CPT_PROCEDURE_CODE_CONFLICT_FILTER_MAX_RESULTS}. */
      truncated: boolean;
    }
  | {
      status: "verification_failed";
      error: unknown;
    };

type ValidateCodeOutcome = { status: "ok"; conflicts: boolean } | { status: "unavailable" };

/**
 * Validates the code against the billable procedure codes ValueSet once per code system it could
 * be billed under: CPT always, and HCPCS only for a HCPCS Level II code, so HCPCS modifiers never
 * conflict. A conflict in any system is a conflict. Without one, every system must answer, or the
 * result is `unavailable`.
 *
 * @param medplum Medplum client
 * @param code Trimmed procedure code
 * @returns `ok` with the conflict, or `unavailable` when a system gave no answer
 */
async function checkBillableProcedureCodeViaValidateCodeOperation(
  medplum: MedplumClient,
  code: string,
): Promise<ValidateCodeOutcome> {
  const systems = isHcpcsLevelIICode(code)
    ? [CPT_CODE_SYSTEM, HCPCS_CODE_SYSTEM]
    : [CPT_CODE_SYSTEM];
  const outcomes = await Promise.all(
    systems.map((system) =>
      validateCodingViaValidateCodeOperation(medplum, ProceduresValueSets.BillableProcedureCodes, {
        system,
        code,
      }),
    ),
  );
  if (outcomes.some((outcome) => outcome.status === "ok" && outcome.conflicts))
    return { status: "ok", conflicts: true };
  if (outcomes.every((outcome) => outcome.status === "ok"))
    return { status: "ok", conflicts: false };
  return { status: "unavailable" };
}

/**
 * Validates one coding with ValueSet `$validate-code` (see Medplum terminology docs). Returns
 * `unavailable` when the server fails or gives no boolean `result`.
 */
async function validateCodingViaValidateCodeOperation(
  medplum: MedplumClient,
  valueSetUrl: string,
  coding: Coding,
): Promise<ValidateCodeOutcome> {
  try {
    const body: FhirParameters = {
      resourceType: "Parameters",
      parameter: [
        { name: "url", valueUri: valueSetUrl },
        { name: "coding", valueCoding: coding },
      ],
    };
    const response = await medplum.post(medplum.fhirUrl(VALUE_SET_VALIDATE_CODE_PATH), body);
    if (
      typeof response !== "object" ||
      response === null ||
      (response as { resourceType?: string }).resourceType !== "Parameters"
    ) {
      return { status: "unavailable" };
    }
    const parameters = response as FhirParameters;
    const resultParameter = parameters.parameter?.find((parameter) => parameter.name === "result");
    if (typeof resultParameter?.valueBoolean !== "boolean") {
      return { status: "unavailable" };
    }
    return { status: "ok", conflicts: resultParameter.valueBoolean };
  } catch (error) {
    Sentry.captureException(error, {
      level: "warning",
      tags: { feature: "custom-procedure-cpt-conflict" },
      extra: {
        operation: "validate-code",
        valueSetUrl,
        note: "Falling back to ValueSet $expand for conflict check",
      },
    });
    return { status: "unavailable" };
  }
}

/**
 * Checks whether a practice custom procedure code duplicates a CPT or HCPCS Level II code in the
 * billable procedure codes ValueSet, the same codes the procedure code search offers. User input
 * is trimmed so it compares consistently to terminology `code` values (which
 * do not include leading/trailing spaces).
 *
 * Prefers the FHIR `ValueSet/$validate-code` operation (no truncation); uses expansion only when
 * validate is unavailable or returns an inconclusive shape.
 *
 * @param medplum Medplum client
 * @param code Procedure code as entered by the user
 */
export async function checkCustomProcedureCodeAgainstCpt(
  medplum: MedplumClient,
  code: string,
): Promise<CustomProcedureCptCodeCheckResult> {
  const trimmed = code.trim();
  if (!trimmed) {
    return { status: "verified", conflicts: false, truncated: false };
  }

  const validateOutcome = await checkBillableProcedureCodeViaValidateCodeOperation(
    medplum,
    trimmed,
  );

  if (validateOutcome.status === "ok") {
    return { status: "verified", conflicts: validateOutcome.conflicts, truncated: false };
  }

  try {
    const codings = await getValueSetOptions(medplum, ProceduresValueSets.BillableProcedureCodes, {
      filter: trimmed,
      count: CPT_PROCEDURE_CODE_CONFLICT_FILTER_MAX_RESULTS,
    });
    const conflicts = codings.some(
      (coding) => coding.code === trimmed && isBillableHostedCoding(coding),
    );
    const truncated = codings.length >= CPT_PROCEDURE_CODE_CONFLICT_FILTER_MAX_RESULTS;
    return { status: "verified", conflicts, truncated };
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: "custom-procedure-cpt-conflict" },
      extra: { operation: "expand-cpt-procedure-value-set" },
    });
    return { status: "verification_failed", error };
  }
}
